// ink-component
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import isNil from 'lodash/isNil';
import styled, { ThemeProvider, css } from 'styled-components';

import useWindowWidth from '../../hooks/useWindowWidth';
import getGlobalProps from '../../helpers/constants/global-props';
import setFocus from '../../helpers/dom/setFocus';

import ControllerError from '../_common/Controller/components/ControllerError/ControllerError';
import Text from '../Typography/Text/Text';
import Box from '../Box/Box';

import { autographCore, autographArrow, autographInput, autographInputError } from './styles';

const AutographWrapper = styled.div`
  ${autographCore};
`;

const AutographArrow = styled.label`
  ${autographArrow}
`;

const AutographInput = styled.input`
  ${autographInput}
  ${({ theme }) =>
    theme.error &&
    css`
      ${autographInputError}
    `}
`;

type Meta = {
  initial: string;
};

type Input = {
  value: string;
  onChange: (value: string) => void;
};

export interface AutographProps {
  /** Unique identifier */
  id?: string;
  /** Expected name to be signed */
  name?: string;
  /** Autofill the input with a value. This should be avoid, since most times the user should actually sign it. */
  value: string;
  /** Custom node for help text */
  helpText?: ReactNode;
  /** Boolean for default error message when `name` is provided. Also accepts a node for custom text. */
  error?: boolean | ReactNode;
  /** onChange callback */
  onChange?: (value: string) => void;
  /** onBlur callback */
  onBlur?: (value: string) => void;
  /** Disables the autoFocus attribute */
  preventAutoFocus?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  meta: Meta;
  /** @ignore */
  input: Input;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Allows users to input electronic signatures
 * @categories form
 * @aka signature, dotted line
 */
function Autograph(props: AutographProps) {
  const initialValue = props.input.value || props.value || props.meta.initial || '';
  const [value, setValue] = useState(initialValue);
  const [valid, setValid] = useState<boolean>(false);
  const { isMobile } = useWindowWidth();

  useEffect(() => {
    if (!isNil(props.value)) {
      setValue(props.value);
    } else if (!isNil(props.input.value)) {
      setValue(props.input.value);
    }
  }, [props.value, props.input.value]);

  const handleChange = e => {
    const { onChange, input, name } = props;
    const { value } = e.target;

    setValue(value);

    if (onChange) onChange(value);
    if (input.onChange) input.onChange(value);

    if (valid !== (value === name)) {
      setValid(value === name);
    }
  };

  const handleBlur = e => {
    const { onBlur, name } = props;
    const { value } = e.target;

    setValue(value);

    if (onBlur) onBlur(value);

    if (valid !== (value === name)) {
      setValid(value === name);
    }
  };

  const renderHelpText = () => {
    const { name, helpText } = props;

    if (helpText)
      return (
        <Box top="4">
          <Text trim>{helpText}</Text>
        </Box>
      );
    if (name)
      return (
        <Box top="4">
          <Text trim>Sign as "{name}"</Text>
        </Box>
      );
    return '';
  };

  const renderError = () => {
    const { error, name } = props;
    if (typeof error === 'boolean') {
      if (name) {
        return (
          <Box top="4">
            <ControllerError>This field is required. Sign as "{name}"</ControllerError>
          </Box>
        );
      }
      return console.error(
        'Passing "props.error" as a boolean without "props.name" evaluates to true, but without any output message',
      );
    }
    return (
      <Box top="4">
        <ControllerError>{error}</ControllerError>
      </Box>
    );
  };

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!props.preventAutoFocus) setFocus(inputRef);
  }, [inputRef, props.preventAutoFocus]);

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        error: props.error,
      })}
    >
      <AutographWrapper {...globalProps} data-testid={props['data-testid']}>
        {!isMobile && <AutographArrow htmlFor={props.id}>Sign here</AutographArrow>}
        <AutographInput
          aria-label="Sign here"
          id={props.id}
          ref={inputRef}
          autoFocus={!props.preventAutoFocus}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={handleChange}
          onBlur={handleBlur}
          value={value}
        />
        {props.error ? renderError() : renderHelpText()}
      </AutographWrapper>
    </ThemeProvider>
  );
}

Autograph.defaultProps = {
  id: undefined,
  name: undefined,
  value: undefined,
  helpText: undefined,
  error: undefined,
  preventAutoFocus: false,
  onChange: undefined,
  'data-testid': undefined,
  input: {},
  meta: {},
};

Autograph.displayName = 'Autograph';

export default Autograph;
